/**
 * TA v4.3 — Top-level execution shell.
 *
 * Owns the state machine + server sync. Renders the active phase view based on `ta_active_phase`:
 *   0 → <TAIntroScreen/>
 *   1 → <TAPhaseRunner phase={1} />
 *   2 → <TAPhaseRunner phase={2} />   (preceded by 1→2 transition once)
 *   3 → <TAPhaseRunner phase={3} />   (preceded by 2→3 transition once)
 *   4 → <TASubmitted/>
 *
 * Phase transitions are a purely client-side UX layer. The server already advanced
 * the candidate to phase N+1 when they hit `/phase/N/submit`, so the new-phase clock is
 * already running. The transition screen acknowledges this honestly to the candidate and
 * is dismissed once per phase via sessionStorage.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle } from "lucide-react";
import { resolveBackendBase } from "../../lib/api";

import TAIntroScreen from "./TAIntroScreen";
import TAPhaseRunner from "./TAPhaseRunner";
import TATransitionScreen from "./TATransitionScreen";

const API_URL = `${resolveBackendBase()}/api/v2`;

function transitionKey(token, toPhase) {
    return `ta:transition-seen:${token}:${toPhase}`;
}

export default function TAExecutionPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [state, setState] = useState(null);
    const [beginning, setBeginning] = useState(false);
    const [beginError, setBeginError] = useState(null);
    // Track transition acknowledgements per phase (and persist across refresh)
    const [transitionDismissed, setTransitionDismissed] = useState(() => ({
        2: typeof window !== "undefined" && window.sessionStorage.getItem(transitionKey(window.location.pathname, 2)) === "1",
        3: typeof window !== "undefined" && window.sessionStorage.getItem(transitionKey(window.location.pathname, 3)) === "1",
    }));

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API_URL}/assessment/ta/${token}/state`);
            setState(r.data);
            return r.data;
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load assessment");
        }
    }, [token]);

    useEffect(() => {
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const begin = async () => {
        setBeginning(true);
        setBeginError(null);
        try {
            await axios.post(`${API_URL}/assessment/ta/${token}/begin`);
            await refresh();
        } catch (e) {
            setBeginError(e.response?.data?.detail || "Could not start the assessment. Please retry.");
        }
        setBeginning(false);
    };

    const dismissTransition = (toPhase) => {
        try {
            window.sessionStorage.setItem(transitionKey(window.location.pathname, toPhase), "1");
        } catch (_) {
            // ignore storage errors
        }
        setTransitionDismissed((d) => ({ ...d, [toPhase]: true }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="bg-card border border-border/40 p-8 max-w-md text-center" data-testid="ta-error-screen">
                    <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
                    <h1 className="text-lg font-display font-bold text-white mb-2">Unable to load assessment</h1>
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    const phase = state?.ta_active_phase ?? 0;

    if (state?.status === "completed" || phase === 4) {
        return <TASubmittedScreen state={state} />;
    }

    if (phase === 0) {
        return <TAIntroScreen data={state} onBegin={begin} beginning={beginning} error={beginError} />;
    }

    // Show transition interstitial once per new phase
    if ((phase === 2 || phase === 3) && !transitionDismissed[phase]) {
        return (
            <TATransitionScreen
                fromPhase={phase - 1}
                toPhase={phase}
                state={state}
                onContinue={() => dismissTransition(phase)}
            />
        );
    }

    if (phase === 1 || phase === 2 || phase === 3) {
        return <TAPhaseRunner phase={phase} state={state} onPhaseAdvanced={refresh} />;
    }

    return null;
}

function TASubmittedScreen({ state }) {
    const phases = state?.ta_phase_state || {};
    const meta = state?.template?.content?.metadata || {};
    const phaseRows = [
        { key: "phase1", label: "Phase 1 · Rapid Fire" },
        { key: "phase2", label: "Phase 2 · Applied Warfare" },
        { key: "phase3", label: "Phase 3 · Judgment Under Fire" },
    ];
    const fmtElapsed = (ps) => {
        if (!ps?.started_at || !ps?.submitted_at) return "—";
        const s = (new Date(ps.submitted_at) - new Date(ps.started_at)) / 1000;
        const m = Math.floor(s / 60);
        const r = Math.floor(s % 60);
        return `${m}m ${String(r).padStart(2, "0")}s`;
    };
    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
            <div className="max-w-xl w-full" data-testid="ta-submitted-screen">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.25em] font-mono text-emerald-300">Assessment submitted</p>
                </div>
                <h1 className="text-3xl font-display font-bold text-white mb-3 leading-tight">
                    Thank you. We've got everything.
                </h1>
                <p className="text-sm text-gray-400 leading-relaxed mb-8 max-w-md">
                    Your responses, telemetry, and per-phase timing have been recorded. THCO will follow up directly. You can close this tab.
                </p>

                <div className="border border-border/40 bg-card p-5 mb-6">
                    <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-3">Your run</p>
                    <div className="space-y-2.5">
                        {phaseRows.map((r) => {
                            const ps = phases[r.key] || {};
                            const late = ps.late;
                            return (
                                <div key={r.key} className="flex items-center justify-between text-sm" data-testid={`ta-submitted-${r.key}`}>
                                    <span className="text-gray-300">{r.label}</span>
                                    <span className={`font-mono text-xs ${late ? "text-amber-300" : "text-gray-400"}`}>
                                        {fmtElapsed(ps)}{late && " · late"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <p className="text-[11px] text-gray-600 font-mono">
                    Assessment ID: <span className="text-gray-400">{meta.assessment_id || "—"}</span>
                </p>
            </div>
        </div>
    );
}
